import { useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { triggerIntegrationSync } from '@/lib/integrationSync';
import { openGoogleDriveFolderPicker, saveGoogleDriveFolders } from '@/lib/googleDrivePicker';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  RefreshCw,
  Upload,
  FileSpreadsheet,
  FileText,

  Link2,
  CheckCircle2,
  XCircle,
  Clock,
  Settings as SettingsIcon,
  Trash2,
  Eraser,
  Play,
  ShieldCheck,
  FolderOpen
} from 'lucide-react';
import { format } from 'date-fns';

// Integration type definitions
const INTEGRATION_TYPES = {
  jira: { name: 'Jira', icon: '🔷', category: 'project_management', color: 'bg-blue-500' },
  azure_devops: { name: 'Azure DevOps', icon: '🔶', category: 'project_management', color: 'bg-sky-500' },
  servicenow: { name: 'ServiceNow PPM', icon: '🟢', category: 'project_management', color: 'bg-green-500' },
  sharepoint: { name: 'SharePoint', icon: '📁', category: 'microsoft_365', color: 'bg-teal-500' },
  planner: { name: 'Microsoft Planner', icon: '📋', category: 'microsoft_365', color: 'bg-green-600' },
  outlook: { name: 'Outlook / Email', icon: '📧', category: 'microsoft_365', color: 'bg-blue-500' },
  microsoft_project: { name: 'Microsoft Project', icon: '📊', category: 'project_management', color: 'bg-emerald-500' },
  onedrive_documents: { name: 'OneDrive Documents', icon: '📄', category: 'microsoft_365', color: 'bg-indigo-500' },
  google_drive: { name: 'Google Drive', icon: '🗂️', category: 'google', color: 'bg-yellow-500' }
};

type IntegrationType = keyof typeof INTEGRATION_TYPES;

// Types that write into projects_sync/tasks_sync/emails_sync — matches
// SYNCED_DATA_SOURCES in the integration-credentials edge function.
// microsoft_project has no live connector (file-import only), so it's
// excluded — there's nothing for "Clear Synced Data" to clear.
const CLEARABLE_INTEGRATION_TYPES = new Set(['jira', 'azure_devops', 'servicenow', 'sharepoint', 'planner', 'outlook', 'onedrive_documents', 'google_drive']);

interface Integration {
  id: string;
  integration_type: string;
  name: string;
  config: Record<string, unknown>;
  is_active: boolean;
  sync_frequency: string;
  last_sync_at: string | null;
  created_at: string;
}

interface SyncLog {
  id: string;
  integration_id: string;
  status: string;
  records_synced: number;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

interface FileImport {
  id: string;
  file_name: string;
  file_type: string;
  status: string;
  records_imported: number;
  created_at: string;
}

export default function Integrations() {
  const [selectedTab, setSelectedTab] = useState('connections');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newIntegrationType, setNewIntegrationType] = useState<IntegrationType | ''>('');
  const [configForm, setConfigForm] = useState<Record<string, string>>({});
  const [editingIntegrationId, setEditingIntegrationId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null);
  const [deleteAlsoClearData, setDeleteAlsoClearData] = useState(false);
  const [clearDataTarget, setClearDataTarget] = useState<Integration | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch integrations
  const { data: integrations = [], isLoading: loadingIntegrations } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integration_configs')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Integration[];
    }
  });

  // Fetch sync logs
  const { data: syncLogs = [] } = useQuery({
    queryKey: ['sync-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integration_sync_logs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as SyncLog[];
    }
  });

  // Fetch file imports
  const { data: fileImports = [] } = useQuery({
    queryKey: ['file-imports'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('file_imports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data as FileImport[];
    }
  });

  // Fetch this tenant's Microsoft 365 admin-consent status. Resolves the
  // tenant via the auth_tenant_id() RPC (same RLS-trusted source every
  // server-side function already uses) rather than useAuth().tenantId's
  // client-side JWT parsing, which isn't reliably populated on this page.
  const { data: msGraphConnection, isLoading: loadingMsGraph } = useQuery({
    queryKey: ['ms-graph-connection'],
    queryFn: async () => {
      const { data: tid, error: tidError } = await supabase.rpc('auth_tenant_id');
      if (tidError) throw tidError;
      if (!tid) return null;

      const { data, error } = await supabase
        .from('tenants')
        .select('ms_graph_tenant_id, ms_graph_consented_at')
        .eq('id', tid)
        .maybeSingle();
      if (error) throw error;
      return data;
    }
  });

  // Start the one-click Microsoft 365 admin-consent flow
  const connectMicrosoft365 = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('graph-consent-start');
      if (error) throw error;
      return data as { url: string };
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast({ title: 'Could not start Microsoft 365 connection', description: error.message, variant: 'destructive' });
    }
  });

  // Start the per-connection "Connect with Google" OAuth flow for a
  // google_drive integration row. Unlike connectMicrosoft365 (tenant-wide,
  // no integrationId needed), this is per-row: the refresh token Google
  // hands back gets written onto this specific integration_configs row.
  const connectGoogleDrive = useMutation({
    mutationFn: async (integrationId: string) => {
      const { data, error } = await supabase.functions.invoke('google-consent-start', {
        body: { integrationId },
      });
      if (error) throw error;
      return data as { url: string };
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error) => {
      toast({ title: 'Could not start Google Drive connection', description: error.message, variant: 'destructive' });
    }
  });

  // Open the Google Picker to (re)select which folder(s) a connected
  // google_drive integration may sync — usable both right after connecting
  // and any time later to change the selection.
  const selectGoogleDriveFolders = useMutation({
    mutationFn: async (integrationId: string) => {
      const folders = await openGoogleDriveFolderPicker(integrationId);
      if (!folders || folders.length === 0) return null;
      await saveGoogleDriveFolders(integrationId, folders);
      return folders;
    },
    onSuccess: (folders) => {
      if (!folders) return;
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast({ title: 'Folders updated', description: `Now syncing ${folders.length} folder${folders.length === 1 ? '' : 's'}.` });
    },
    onError: (error) => {
      toast({ title: 'Could not select folders', description: error.message, variant: 'destructive' });
    }
  });

  // Create integration mutation
  const createIntegration = useMutation({
    mutationFn: async (integration: { integration_type: string; name: string; config: Record<string, unknown> }) => {
      const { data, error } = await supabase.functions.invoke('integration-credentials', {
        body: {
          action: 'save',
          integrationType: integration.integration_type,
          name: integration.name,
          config: integration.config,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setShowAddDialog(false);
      setNewIntegrationType('');
      setConfigForm({});
      toast({ title: 'Integration added', description: 'Connection configured successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Update integration mutation (settings/edit)
  const updateIntegration = useMutation({
    mutationFn: async (integration: { id: string; name: string; config: Record<string, unknown> }) => {
      const integrationType = integrations.find((i) => i.id === integration.id)?.integration_type;
      const { error } = await supabase.functions.invoke('integration-credentials', {
        body: {
          action: 'save',
          integrationId: integration.id,
          integrationType,
          name: integration.name,
          config: integration.config,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setShowAddDialog(false);
      setEditingIntegrationId(null);
      setNewIntegrationType('');
      setConfigForm({});
      toast({ title: 'Integration updated', description: 'Connection settings saved' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  });

  // Sync integration mutation
  const syncIntegration = useMutation({
    mutationFn: async (integrationId: string) => {
      const integration = integrations.find(i => i.id === integrationId);
      if (!integration) throw new Error('Integration not found');
      return triggerIntegrationSync(integrationId, integration.integration_type);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['sync-logs'] });
      toast({ 
        title: 'Sync complete', 
        description: `Synced ${data?.data?.recordsSynced || 0} records` 
      });
    },
    onError: (error) => {
      toast({ title: 'Sync failed', description: error.message, variant: 'destructive' });
    }
  });

  const invalidateSyncedDataQueries = () => {
    queryClient.invalidateQueries({
      predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('synced-')
    });
  };

  // Clears every synced row (projects_sync/tasks_sync/meetings_sync) for an
  // integration TYPE — scoped by `source`, not by the specific integration
  // row, since synced data isn't tagged with an integration_id today. If a
  // tenant has more than one integration of the same type, this clears all
  // of their data at once (surfaced as a warning in the confirm dialogs).
  const clearSyncedData = useMutation({
    mutationFn: async (integrationType: string) => {
      const { data, error } = await supabase.functions.invoke('integration-credentials', {
        body: { action: 'clear_synced_data', integrationType },
      });
      if (error) throw error;
      return data as { totalCleared: number };
    },
    onSuccess: (data) => {
      invalidateSyncedDataQueries();
      toast({ title: 'Synced data cleared', description: `Removed ${data?.totalCleared ?? 0} synced records.` });
    },
    onError: (error) => {
      toast({ title: 'Clear failed', description: error.message, variant: 'destructive' });
    }
  });

  // Delete integration mutation
  const deleteIntegration = useMutation({
    mutationFn: async ({ integrationId, alsoClearData, integrationType }: { integrationId: string; alsoClearData: boolean; integrationType: string }) => {
      const { error } = await supabase.functions.invoke('integration-credentials', {
        body: { action: 'delete', integrationId },
      });
      if (error) throw error;
      if (alsoClearData) {
        await clearSyncedData.mutateAsync(integrationType);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast({ title: 'Integration removed' });
    },
    onError: (error) => {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    }
  });

  // File upload handler
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileType = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv', 'docx'].includes(fileType || '')) {
      toast({ title: 'Unsupported file', description: 'Please upload Excel (.xlsx, .xls), CSV, or Word (.docx) files', variant: 'destructive' });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload file to storage
      const filePath = `${user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('file-imports')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Bucket is private — create a short-lived signed URL for parsing
      const { data: signedData, error: signedError } = await supabase.storage
        .from('file-imports')
        .createSignedUrl(filePath, 60 * 10);

      if (signedError || !signedData) throw signedError ?? new Error('Failed to create signed URL');

      // Create file import record (store the storage path, not a public URL)
      const { data: importRecord, error: insertError } = await supabase
        .from('file_imports')
        .insert({
          user_id: user.id,
          file_name: file.name,
          file_type: fileType,
          file_url: filePath,
          status: 'uploaded'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Trigger parsing (pass the signed URL so the function can fetch the file)
      const { data: parseResult, error: parseError } = await supabase.functions.invoke('parse-file', {
        body: {
          fileUrl: signedData.signedUrl,
          fileType,
          fileImportId: importRecord.id
        }
      });

      if (parseError) throw parseError;

      // Now import the parsed data into dashboard tables
      toast({ title: 'File parsed', description: 'Importing data to dashboard...' });

      const { data: importResult, error: importError } = await supabase.functions.invoke('import-file-data', {
        body: {
          fileImportId: importRecord.id,
          dataType: 'auto', // Auto-detect projects/tasks/kpis
          parsedData: parseResult?.data
        }
      });

      if (importError) {
        console.error('Import error:', importError);
        toast({ title: 'Import warning', description: 'File parsed but import had issues', variant: 'destructive' });
      }

      queryClient.invalidateQueries({ queryKey: ['file-imports'] });
      queryClient.invalidateQueries({ queryKey: ['synced-projects'] });
      queryClient.invalidateQueries({ queryKey: ['synced-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['synced-kpis'] });
      
      const imported = importResult?.data?.imported || 0;
      const dataType = importResult?.data?.dataType || 'records';
      
      toast({ 
        title: 'Import complete', 
        description: `Imported ${imported} ${dataType} from ${file.name}` 
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload file';
      toast({ title: 'Upload failed', description: errorMessage, variant: 'destructive' });
    }
  };

  const getConfigFields = (type: IntegrationType): { key: string; label: string; type: string; placeholder: string; helpText?: string }[] => {
    switch (type) {
      case 'jira':
        return [
          { key: 'domain', label: 'Jira Domain', type: 'text', placeholder: 'your-company (without .atlassian.net)' },
          { key: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com' },
          { key: 'apiToken', label: 'API Token', type: 'password', placeholder: 'Your Jira API token', helpText: 'Generate at id.atlassian.com → Security → API tokens' }
        ];
      case 'azure_devops':
        return [
          { key: 'organization', label: 'Organization', type: 'text', placeholder: 'your-org' },
          { key: 'pat', label: 'Personal Access Token', type: 'password', placeholder: 'Your PAT', helpText: 'Generate at dev.azure.com → User Settings → Personal Access Tokens' },
          { key: 'project', label: 'Project (optional)', type: 'text', placeholder: 'Specific project name' }
        ];
      case 'servicenow':
        return [
          { key: 'instance', label: 'Instance', type: 'text', placeholder: 'your-instance (without .service-now.com)' },
          { key: 'username', label: 'Username', type: 'text', placeholder: 'admin' },
          { key: 'password', label: 'Password', type: 'password', placeholder: 'Your password' }
        ];
      case 'sharepoint':
        return [
          { key: 'siteId', label: 'SharePoint Site ID', type: 'text', placeholder: 'e.g. contoso.sharepoint.com,<site-guid>,<web-guid>', helpText: 'Authenticates via your organization’s Microsoft Entra app — no token needed here.' }
        ];
      case 'planner':
        return [
          { key: 'groupId', label: 'Microsoft 365 Group ID', type: 'text', placeholder: 'The group that owns the Planner plan(s)', helpText: 'Authenticates via your organization’s Microsoft Entra app — no token needed here.' }
        ];
      case 'outlook':
        return [
          { key: 'mailbox', label: 'Mailbox', type: 'text', placeholder: 'e.g. shared-inbox@yourcompany.com', helpText: 'The mailbox address (user principal name or shared mailbox) to sync emails from. Authenticates via your organization’s Microsoft Entra app — no token needed here.' }
        ];
      case 'microsoft_project':
        return [
          { key: 'webhookUrl', label: 'Webhook URL', type: 'text', placeholder: 'Power Automate webhook URL', helpText: 'See Power Automate template in Settings → Resources' }
        ];
      case 'onedrive_documents':
        return [
          { key: 'driveId', label: 'OneDrive Drive ID', type: 'text', placeholder: 'The drive to sync documents from', helpText: 'Authenticates via your organization’s Microsoft Entra app — no token needed here.' },
          { key: 'folderId', label: 'Folder ID (optional)', type: 'text', placeholder: 'Limit sync to a specific folder' }
        ];
      case 'google_drive':
        // No config fields — this is an OAuth-only connection. The
        // connection is authorized via the per-row "Connect with Google"
        // button shown on the card after it's created (see the card render
        // below), not through this form.
        return [];
      default:
        return [];
    }
  };

  const getMicrosoftOAuthNote = (type: IntegrationType): string | null => {
    if (['sharepoint', 'planner', 'outlook', 'onedrive_documents'].includes(type)) {
      return 'Microsoft 365 integrations authenticate as your organization’s Microsoft Entra app registration (configured by an admin) — you don’t need to provide a token.';
    }
    if (type === 'google_drive') {
      return 'Google Drive connects via OAuth — after adding this connection, click "Connect with Google" on its card to authorize access to Docs, Sheets, and Slides.';
    }
    return null;
  };

  const handleAddIntegration = () => {
    if (!newIntegrationType) return;

    const typeInfo = INTEGRATION_TYPES[newIntegrationType];
    if (editingIntegrationId) {
      updateIntegration.mutate({
        id: editingIntegrationId,
        name: configForm.name || typeInfo.name,
        config: configForm
      });
      return;
    }
    createIntegration.mutate({
      integration_type: newIntegrationType,
      name: configForm.name || typeInfo.name,
      config: configForm
    });
  };

  const handleOpenEditIntegration = (integration: Integration) => {
    setEditingIntegrationId(integration.id);
    setNewIntegrationType(integration.integration_type as IntegrationType);
    const fields = getConfigFields(integration.integration_type as IntegrationType);
    const secretKeys = new Set(fields.filter((f) => f.type === 'password').map((f) => f.key));
    const sanitizedConfig = Object.fromEntries(
      Object.entries(integration.config as Record<string, string>).filter(([key]) => !secretKeys.has(key))
    );
    setConfigForm({ name: integration.name, ...sanitizedConfig });
    setShowAddDialog(true);
  };

  const handleAddDialogOpenChange = (open: boolean) => {
    setShowAddDialog(open);
    if (!open) {
      setEditingIntegrationId(null);
      setNewIntegrationType('');
      setConfigForm({});
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'running':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <AppShell activeView="integrations">
      <div className="max-w-7xl mx-auto space-y-6">
          <h1 className="text-2xl font-semibold">Data Integrations</h1>
          <Card>
            <CardContent className="py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center text-white ${msGraphConnection?.ms_graph_tenant_id ? 'bg-emerald-600' : 'bg-muted-foreground/40'}`}>
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">Microsoft 365</p>
                    {!loadingMsGraph && msGraphConnection?.ms_graph_tenant_id && (
                      <Badge variant="secondary" className="gap-1 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="h-3 w-3" />
                        Connected
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {loadingMsGraph
                      ? 'Checking connection…'
                      : msGraphConnection?.ms_graph_tenant_id
                        ? `Since ${format(new Date(msGraphConnection.ms_graph_consented_at!), 'MMM d, yyyy')} — required for SharePoint, Planner, Outlook & Teams meetings`
                        : 'Not connected — required before adding SharePoint, Planner, Outlook, or Teams meetings integrations'}
                  </p>
                </div>
              </div>
              {msGraphConnection?.ms_graph_tenant_id ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  title="Re-run Microsoft admin consent — only needed if permissions changed or access was revoked"
                  onClick={() => connectMicrosoft365.mutate()}
                  disabled={connectMicrosoft365.isPending || loadingMsGraph}
                >
                  {connectMicrosoft365.isPending ? 'Redirecting…' : 'Reconnect'}
                </Button>
              ) : (
                <Button
                  onClick={() => connectMicrosoft365.mutate()}
                  disabled={connectMicrosoft365.isPending || loadingMsGraph}
                >
                  {connectMicrosoft365.isPending ? 'Redirecting…' : 'Connect Microsoft 365'}
                </Button>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground">
                Connect external data sources and import files
              </p>
            </div>
              <div className="flex gap-2">
                <label htmlFor="file-upload">
                  <Button variant="outline" asChild>
                    <span className="cursor-pointer">
                      <Upload className="h-4 w-4 me-2" />
                      Upload File
                    </span>
                  </Button>
                </label>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls,.csv,.docx"
                  onChange={handleFileUpload}
                />
                <Dialog open={showAddDialog} onOpenChange={handleAddDialogOpenChange}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 me-2" />
                      Add Connection
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingIntegrationId ? 'Edit Integration' : 'Add Integration'}</DialogTitle>
                      <DialogDescription>
                        {editingIntegrationId ? 'Update connection settings' : 'Connect to an external data source'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Integration Type</Label>
                        <Select value={newIntegrationType} onValueChange={(v) => setNewIntegrationType(v as IntegrationType)} disabled={!!editingIntegrationId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select integration type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="jira">🔷 Jira</SelectItem>
                            <SelectItem value="azure_devops">🔶 Azure DevOps</SelectItem>
                            <SelectItem value="servicenow">🟢 ServiceNow PPM</SelectItem>
                            <SelectItem value="sharepoint">📁 SharePoint</SelectItem>
                            <SelectItem value="planner">📋 Microsoft Planner</SelectItem>
                            <SelectItem value="outlook">📧 Outlook / Email</SelectItem>
                            <SelectItem value="microsoft_project">📊 Microsoft Project</SelectItem>
                            <SelectItem value="onedrive_documents">📄 OneDrive Documents</SelectItem>
                            <SelectItem value="google_drive">🗂️ Google Drive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {newIntegrationType && (
                        <>
                          <div>
                            <Label>Connection Name</Label>
                            <Input
                              placeholder={`My ${INTEGRATION_TYPES[newIntegrationType].name}`}
                              value={configForm.name || ''}
                              onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })}
                            />
                          </div>

                          {getMicrosoftOAuthNote(newIntegrationType) && (
                            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm text-amber-700 dark:text-amber-400">
                              ⚠️ {getMicrosoftOAuthNote(newIntegrationType)}
                            </div>
                          )}

                          {getConfigFields(newIntegrationType).map((field) => (
                            <div key={field.key}>
                              <Label>{field.label}</Label>
                              <Input
                                type={field.type}
                                placeholder={field.placeholder}
                                value={configForm[field.key] || ''}
                                onChange={(e) => setConfigForm({ ...configForm, [field.key]: e.target.value })}
                              />
                              {field.helpText && (
                                <p className="text-xs text-muted-foreground mt-1">{field.helpText}</p>
                              )}
                              {field.type === 'password' && editingIntegrationId && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Leave blank to keep the current value.
                                </p>
                              )}
                            </div>
                          ))}

                          <Button
                            className="w-full"
                            onClick={handleAddIntegration}
                            disabled={createIntegration.isPending || updateIntegration.isPending}
                          >
                            {editingIntegrationId
                              ? (updateIntegration.isPending ? 'Saving...' : 'Save Changes')
                              : (createIntegration.isPending ? 'Adding...' : 'Add Integration')}
                          </Button>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
              <TabsList>
                <TabsTrigger value="connections">Connections</TabsTrigger>
                <TabsTrigger value="files">File Imports</TabsTrigger>
                <TabsTrigger value="history">Sync History</TabsTrigger>
              </TabsList>

              <TabsContent value="connections" className="mt-6">
                {loadingIntegrations ? (
                  <div className="text-center py-12 text-muted-foreground">Loading integrations...</div>
                ) : integrations.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Link2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="font-medium text-lg mb-2">No integrations configured</h3>
                      <p className="text-muted-foreground mb-4">
                        Connect to Jira, Azure DevOps, ServiceNow, or Microsoft 365 to sync project data
                      </p>
                      <Button onClick={() => setShowAddDialog(true)}>
                        <Plus className="h-4 w-4 me-2" />
                        Add First Integration
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {integrations.map((integration) => {
                      const typeInfo = INTEGRATION_TYPES[integration.integration_type as IntegrationType];
                      const integrationLogs = syncLogs
                        .filter((log) => log.integration_id === integration.id)
                        .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
                      let consecutiveFailures = 0;
                      for (const log of integrationLogs) {
                        if (log.status !== 'failed') break;
                        consecutiveFailures++;
                      }
                      return (
                        <Card key={integration.id}>
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`h-10 w-10 rounded-lg ${typeInfo?.color || 'bg-gray-500'} flex items-center justify-center text-white text-lg`}>
                                  {typeInfo?.icon || '📦'}
                                </div>
                                <div>
                                  <CardTitle className="text-base">{integration.name}</CardTitle>
                                  <CardDescription>{typeInfo?.name || integration.integration_type}</CardDescription>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <Badge variant={integration.is_active ? 'default' : 'secondary'}>
                                  {integration.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                                {integration.integration_type === 'google_drive' && !integration.config?.refreshToken && (
                                  <Badge variant="outline" className="text-amber-600 dark:text-amber-400">
                                    Not connected
                                  </Badge>
                                )}
                                {consecutiveFailures > 0 && (
                                  <Badge variant="destructive">
                                    {consecutiveFailures} failed sync{consecutiveFailures === 1 ? '' : 's'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="text-sm text-muted-foreground mb-4">
                              {integration.last_sync_at ? (
                                <>Last synced: {format(new Date(integration.last_sync_at), 'MMM d, h:mm a')}</>
                              ) : (
                                'Never synced'
                              )}
                              {integration.integration_type === 'google_drive' && integration.config?.refreshToken && (
                                <div className="mt-1">
                                  {Array.isArray(integration.config?.folders) && (integration.config.folders as { id: string; name: string }[]).length > 0
                                    ? `Syncing ${(integration.config.folders as { id: string; name: string }[]).length} folder${(integration.config.folders as { id: string; name: string }[]).length === 1 ? '' : 's'}: ${(integration.config.folders as { id: string; name: string }[]).map((f) => f.name).join(', ')}`
                                    : 'No folders selected yet'}
                                </div>
                              )}
                            </div>
                            {integration.integration_type === 'google_drive' && !integration.config?.refreshToken ? (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => connectGoogleDrive.mutate(integration.id)}
                                  disabled={connectGoogleDrive.isPending}
                                >
                                  {connectGoogleDrive.isPending ? 'Redirecting…' : 'Connect with Google'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleOpenEditIntegration(integration)}>
                                  <SettingsIcon className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setDeleteAlsoClearData(false); setDeleteTarget(integration); }}
                                >
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              </div>
                            ) : (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => syncIntegration.mutate(integration.id)}
                                disabled={syncIntegration.isPending}
                              >
                                <Play className="h-3 w-3 me-1" />
                                {consecutiveFailures > 0 ? 'Retry Sync' : 'Sync Now'}
                              </Button>
                              {integration.integration_type === 'google_drive' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title="Select which Drive folders to sync"
                                  onClick={() => selectGoogleDriveFolders.mutate(integration.id)}
                                  disabled={selectGoogleDriveFolders.isPending}
                                >
                                  <FolderOpen className="h-3 w-3 me-1" />
                                  {Array.isArray(integration.config?.folders) && (integration.config.folders as unknown[]).length > 0 ? 'Change folders' : 'Select folders'}
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => handleOpenEditIntegration(integration)}>
                                <SettingsIcon className="h-3 w-3" />
                              </Button>
                              {CLEARABLE_INTEGRATION_TYPES.has(integration.integration_type) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title="Clear synced data"
                                  onClick={() => setClearDataTarget(integration)}
                                >
                                  <Eraser className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setDeleteAlsoClearData(false); setDeleteTarget(integration); }}
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="files" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>File Imports</CardTitle>
                    <CardDescription>
                      Upload Excel, CSV, Word, or PowerPoint files to import project data
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <Card className="border-dashed">
                        <CardContent className="py-6 text-center">
                          <FileSpreadsheet className="h-8 w-8 mx-auto text-green-500 mb-2" />
                          <p className="font-medium">Excel / CSV</p>
                          <p className="text-xs text-muted-foreground">.xlsx, .xls, .csv</p>
                        </CardContent>
                      </Card>
                      <Card className="border-dashed">
                        <CardContent className="py-6 text-center">
                          <FileText className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                          <p className="font-medium">Word</p>
                          <p className="text-xs text-muted-foreground">.docx</p>
                        </CardContent>
                      </Card>
                    </div>

                    {fileImports.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No files imported yet. Upload a file to get started.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {fileImports.map((file) => (
                          <div key={file.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              {file.file_type === 'xlsx' || file.file_type === 'xls' || file.file_type === 'csv' ? (
                                <FileSpreadsheet className="h-5 w-5 text-green-500" />
                              ) : (
                                <FileText className="h-5 w-5 text-blue-500" />
                              )}
                              <div>
                                <p className="font-medium text-sm">{file.file_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {format(new Date(file.created_at), 'MMM d, yyyy h:mm a')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(file.status)}
                              <Badge variant="outline">{file.status}</Badge>
                              {file.records_imported > 0 && (
                                <span className="text-xs text-muted-foreground">
                                  {file.records_imported} records
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="history" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Sync History</CardTitle>
                    <CardDescription>Recent synchronization activity</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {syncLogs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No sync history yet. Sync an integration to see results here.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {syncLogs.map((log) => {
                          const integration = integrations.find(i => i.id === log.integration_id);
                          return (
                            <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center gap-3">
                                {getStatusIcon(log.status)}
                                <div>
                                  <p className="font-medium text-sm">
                                    {integration?.name || 'Unknown Integration'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {format(new Date(log.started_at), 'MMM d, yyyy h:mm a')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant={log.status === 'success' ? 'default' : log.status === 'failed' ? 'destructive' : 'secondary'}>
                                  {log.status}
                                </Badge>
                                {log.records_synced > 0 && (
                                  <span className="text-sm">{log.records_synced} records</span>
                                )}
                                {log.error_message && (
                                  <span className="text-xs text-destructive max-w-[200px] truncate">
                                    {log.error_message}
                                  </span>
                                )}
                                {log.status === 'failed' && integration && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => syncIntegration.mutate(integration.id)}
                                    disabled={syncIntegration.isPending}
                                  >
                                    <RefreshCw className="h-3 w-3 me-1" />
                                    Retry
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>This removes the connection and its stored credentials. It won't affect the app's saved data unless you choose to clear it below.</p>
                  {deleteTarget && CLEARABLE_INTEGRATION_TYPES.has(deleteTarget.integration_type) && (
                    <div className="flex items-start gap-2 rounded-md border p-3">
                      <Checkbox
                        id="also-clear-data"
                        checked={deleteAlsoClearData}
                        onCheckedChange={(checked) => setDeleteAlsoClearData(checked === true)}
                      />
                      <label htmlFor="also-clear-data" className="text-sm leading-snug cursor-pointer">
                        Also clear all synced {INTEGRATION_TYPES[deleteTarget.integration_type as IntegrationType]?.name ?? deleteTarget.integration_type} data (projects/tasks pulled in by any sync).
                        {integrations.filter(i => i.integration_type === deleteTarget.integration_type).length > 1 && (
                          <span className="block text-destructive mt-1">
                            You have {integrations.filter(i => i.integration_type === deleteTarget.integration_type).length} {INTEGRATION_TYPES[deleteTarget.integration_type as IntegrationType]?.name ?? deleteTarget.integration_type} connections — this clears data for all of them, not just this one.
                          </span>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteTarget(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (!deleteTarget) return;
                  deleteIntegration.mutate({
                    integrationId: deleteTarget.id,
                    alsoClearData: deleteAlsoClearData,
                    integrationType: deleteTarget.integration_type,
                  });
                  setDeleteTarget(null);
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!clearDataTarget} onOpenChange={(open) => { if (!open) setClearDataTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear synced data?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes every synced project and task record with source "{clearDataTarget?.integration_type}" —
                {' '}useful before a resync if old data turned out to be stale or malformed. The integration connection itself is kept; run "Sync Now" afterward to repopulate it.
                {clearDataTarget && integrations.filter(i => i.integration_type === clearDataTarget.integration_type).length > 1 && (
                  <span className="block text-destructive mt-2">
                    You have {integrations.filter(i => i.integration_type === clearDataTarget.integration_type).length} {INTEGRATION_TYPES[clearDataTarget.integration_type as IntegrationType]?.name ?? clearDataTarget.integration_type} connections — this clears data for all of them, not just this one.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setClearDataTarget(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (!clearDataTarget) return;
                  clearSyncedData.mutate(clearDataTarget.integration_type);
                  setClearDataTarget(null);
                }}
              >
                Clear Data
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </AppShell>
  );
}
